import type * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { omit } from 'lodash';
import * as React from 'react';
import type { FieldValues, Path } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import {
	cn,
	Checkbox,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@lqa-frontend/ui-system';

function CheckboxFormField<T extends FieldValues>({
	className,
	position = 'after',
	...props
}: ICheckboxFormFieldProps<T>) {
	const form = useFormContext();

	return (
		<FormField
			control={form.control}
			name={props.name}
			render={(renderProps) => (
				<FormItem
					className={cn(
						'w-full gap-8 flex flex-wrap items-center',
						props.itemClassName
					)}
				>
					{position === 'before' && <FormLabel>{props.label}</FormLabel>}
					<FormControl>
						<Checkbox
							checked={renderProps.field.value}
							onCheckedChange={renderProps.field.onChange}
							{...{
								...omit(renderProps.field, [
									'value',
									'onChange',
									'onCheckedChange',
								]),
								...omit(props, ['itemClassName', 'onChange']),
								className,
							}}
						/>
					</FormControl>
					{position === 'after' && <FormLabel>{props.label}</FormLabel>}

					{props.description ? (
						<FormDescription className="basis-full">
							{props.description}
						</FormDescription>
					) : (
						''
					)}
					<FormMessage className="basis-full" />
				</FormItem>
			)}
		/>
	);
}

interface ICheckboxFormFieldProps<
	T extends FieldValues,
> extends React.ComponentProps<typeof CheckboxPrimitive.Root> {
	name: Path<T>;
	description?: string;
	label: string | React.ReactNode;
	itemClassName?: string;
	position?: 'before' | 'after';
}

export { CheckboxFormField };
export type { ICheckboxFormFieldProps };
